import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
  Stack,
  Button,
  FormHelperText,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function PPTReportConfigModal({
  open,
  onClose,
  onGenerate,
  loading = false,
  brandName,
  categories = [],
  llms = [],
  markets = [],
  personas = [],
  dashboards = [], 
  preselectedValues = null, 
}) {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLLMs, setSelectedLLMs] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [errors, setErrors] = useState({});
  
  const syncInProgressRef = useRef(false);

  const getCategoryDashboardId = (categoryName) => {
    const categoryKey = categoryName.replace(/\s+/g, "_");
    return `nav_${categoryKey}`;
  };

  const getCategoryNameFromDashboard = (dashboardId) => {
    if (!dashboardId.startsWith('nav_')) return null;
    const dashboard = dashboards.find(d => d.id === dashboardId);
    return dashboard?.name || null;
  };

  const isCategoryDashboard = (dashboardId) => {
    return dashboardId.startsWith('nav_');
  };

  useEffect(() => {
    if (open) {
      if (preselectedValues) {
        setSelectedCategories(preselectedValues.categories || categories);
        setSelectedLLMs(preselectedValues.llms || llms);
        setSelectedMarkets(preselectedValues.markets || markets);
        setSelectedPersonas(preselectedValues.personas || personas);
        setSelectedDashboards(preselectedValues.dashboards || dashboards.map(d => d.id));
      } else {
        setSelectedCategories(categories.length === 1 ? categories : []);
        setSelectedLLMs(llms.length === 1 ? llms : []);
        setSelectedMarkets(markets.length === 1 ? markets : []);
        setSelectedPersonas(personas.length === 1 ? personas : []);
        setSelectedDashboards([]);
      }
      setErrors({});
      syncInProgressRef.current = false;
    }
  }, [open, categories, llms, markets, personas, dashboards, preselectedValues]);

  const handleCategoriesChange = (e) => {
    if (syncInProgressRef.current) return;
    
    const { value } = e.target;
    const newCategories = typeof value === 'string' ? value.split(',') : value;
    
    syncInProgressRef.current = true;
    setSelectedCategories(newCategories);
    setSelectedDashboards((prevDashboards) => {
      const removedCategories = selectedCategories.filter(
        cat => !newCategories.includes(cat)
      );
      
      if (removedCategories.length === 0) {
        return prevDashboards;
      }
      
      const dashboardsToRemove = removedCategories.map(getCategoryDashboardId);
      
      return prevDashboards.filter(
        dashId => !dashboardsToRemove.includes(dashId)
      );
    });
    
    if (errors.categories) {
      setErrors((prev) => ({ ...prev, categories: false }));
    }
    
    setTimeout(() => {
      syncInProgressRef.current = false;
    }, 0);
  };

  const handleLLMChange = (e) => {
    const { value } = e.target;
    setSelectedLLMs(typeof value === 'string' ? value.split(',') : value);
    if (errors.llms) {
      setErrors((prev) => ({ ...prev, llms: false }));
    }
  };

  const handleMarketChange = (e) => {
    const { value } = e.target;
    setSelectedMarkets(typeof value === 'string' ? value.split(',') : value);
    if (errors.markets) {
      setErrors((prev) => ({ ...prev, markets: false }));
    }
  };

  const handlePersonaChange = (e) => {
    const { value } = e.target;
    setSelectedPersonas(typeof value === 'string' ? value.split(',') : value);
    if (errors.personas) {
      setErrors((prev) => ({ ...prev, personas: false }));
    }
  };

const handleDashboardChange = (e) => {
  if (syncInProgressRef.current) return;
  
  const { value } = e.target;
  const newDashboards = typeof value === 'string' ? value.split(',') : value;
  
  syncInProgressRef.current = true;
  
  setSelectedDashboards(newDashboards);
  
  setSelectedCategories((prevCategories) => {
    const oldCategoryDashboards = selectedDashboards.filter(isCategoryDashboard);
    const newCategoryDashboards = newDashboards.filter(isCategoryDashboard);
    
    const addedDashboards = newCategoryDashboards.filter(
      dashId => !oldCategoryDashboards.includes(dashId)
    );    
    let updatedCategories = [...prevCategories];
    
    addedDashboards.forEach(dashId => {
      const categoryName = getCategoryNameFromDashboard(dashId);
      if (categoryName && !updatedCategories.includes(categoryName)) {
        updatedCategories.push(categoryName);
      }
    });
        
    return updatedCategories;
  });
  
  if (errors.dashboards) {
    setErrors((prev) => ({ ...prev, dashboards: false }));
  }
  
  setTimeout(() => {
    syncInProgressRef.current = false;
  }, 0);
};

  const validateForm = () => {
    const newErrors = {};

    if (selectedCategories.length === 0) {
      newErrors.categories = true;
    }
    if (selectedLLMs.length === 0) {
      newErrors.llms = true;
    }
    if (selectedMarkets.length === 0) {
      newErrors.markets = true;
    }
    if (selectedPersonas.length === 0) {
      newErrors.personas = true;
    }
    if (selectedDashboards.length === 0) {
      newErrors.dashboards = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = () => {
    if (validateForm()) {
      onGenerate({
        categories: selectedCategories,
        llms: selectedLLMs,
        markets: selectedMarkets,
        personas: selectedPersonas,
        dashboards: selectedDashboards,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        PPT Report Configuration {brandName && `- ${brandName}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Categories - Multi-Select */}
          <FormControl fullWidth error={errors.categories}>
            <InputLabel>Categories *</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={handleCategoriesChange}
              input={<OutlinedInput label="Categories *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={(e) => {
                        e.stopPropagation();
                        const newCategories = selectedCategories.filter((item) => item !== value);
                        handleCategoriesChange({ target: { value: newCategories } });
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            {errors.categories && (
              <FormHelperText>Please select at least one category</FormHelperText>
            )}
          </FormControl>

          {/* LLMs - Multi-Select */}
          <FormControl fullWidth error={errors.llms}>
            <InputLabel>LLMs *</InputLabel>
            <Select
              multiple
              value={selectedLLMs}
              onChange={handleLLMChange}
              input={<OutlinedInput label="LLMs *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() =>
                        setSelectedLLMs((prev) => prev.filter((item) => item !== value))
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {llms.map((llm) => (
                <MenuItem key={llm} value={llm}>
                  {llm}
                </MenuItem>
              ))}
            </Select>
            {errors.llms && (
              <FormHelperText>Please select at least one LLM</FormHelperText>
            )}
          </FormControl>

          {/* Markets - Multi-Select */}
          <FormControl fullWidth error={errors.markets}>
            <InputLabel>Markets *</InputLabel>
            <Select
              multiple
              value={selectedMarkets}
              onChange={handleMarketChange}
              input={<OutlinedInput label="Markets *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() =>
                        setSelectedMarkets((prev) =>
                          prev.filter((item) => item !== value)
                        )
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {markets.map((market) => (
                <MenuItem key={market} value={market}>
                  {market}
                </MenuItem>
              ))}
            </Select>
            {errors.markets && (
              <FormHelperText>Please select at least one market</FormHelperText>
            )}
          </FormControl>

          {/* Personas - Multi-Select */}
          <FormControl fullWidth error={errors.personas}>
            <InputLabel>Personas *</InputLabel>
            <Select
              multiple
              value={selectedPersonas}
              onChange={handlePersonaChange}
              input={<OutlinedInput label="Personas *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() =>
                        setSelectedPersonas((prev) =>
                          prev.filter((item) => item !== value)
                        )
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {personas.map((persona) => (
                <MenuItem key={persona} value={persona}>
                  {persona}
                </MenuItem>
              ))}
            </Select>
            {errors.personas && (
              <FormHelperText>Please select at least one persona</FormHelperText>
            )}
          </FormControl>

          {/* Dashboards - Multi-Select */}
          <FormControl fullWidth error={errors.dashboards}>
            <InputLabel>Dashboards *</InputLabel>
            <Select
              multiple
              value={selectedDashboards}
              onChange={handleDashboardChange}
              input={<OutlinedInput label="Dashboards *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const dashboard = dashboards.find((d) => d.id === value);
                    return (
                      <Chip
                        key={value}
                        label={dashboard?.name || value}
                        onDelete={(e) => {
                          e.stopPropagation();
                          const newDashboards = selectedDashboards.filter((item) => item !== value);
                          handleDashboardChange({ target: { value: newDashboards } });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {dashboards.map((dashboard) => (
                <MenuItem key={dashboard.id} value={dashboard.id}>
                  {dashboard.name}
                </MenuItem>
              ))}
            </Select>
            {errors.dashboards && (
              <FormHelperText>Please select at least one dashboard</FormHelperText>
            )}
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Generate Combinations
        </Button>
      </DialogActions>
    </Dialog>
  );
}